import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function FloatingCart({ cartItems = [], removeFromCart, updateQuantity }) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const totalCount = cartItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
  const totalPrice = cartItems.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);

  return (
    <>
      {/* ── Backdrop ── */}
      {isOpen && (
        <div
          onClick={() => setIsOpen(false)}
          style={{
            position: 'fixed', inset: 0,
            backgroundColor: 'rgba(0,0,0,0.4)',
            zIndex: 9998,
          }}
        />
      )}

      {/* ── Slide-in Cart Panel ── */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: isOpen ? 0 : '-400px',
          width: '380px',
          maxWidth: '95vw',
          height: '100vh',
          backgroundColor: '#fff',
          zIndex: 9999,
          boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
          transition: 'right 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: "'Assistant', -apple-system, sans-serif",
        }}
      >
        {/* Panel Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: '1px solid #eee',
          backgroundColor: '#282c3f',
          color: '#fff',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <span style={{ fontWeight: 700, fontSize: '16px', letterSpacing: '0.05em' }}>
              MY CART ({totalCount})
            </span>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            style={{
              background: 'none', border: 'none', color: '#fff',
              cursor: 'pointer', fontSize: '24px', lineHeight: 1, padding: '2px 6px',
            }}
          >×</button>
        </div>

        {/* Items List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {cartItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#aaa' }}>
              <svg width="64" height="64" fill="none" stroke="#ddd" strokeWidth="1.5" viewBox="0 0 24 24" style={{ margin: '0 auto 16px', display: 'block' }}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p style={{ fontSize: '15px', fontWeight: 600, color: '#555', marginBottom: '6px' }}>Cart is empty</p>
              <p style={{ fontSize: '13px' }}>Add some products to continue</p>
            </div>
          ) : (
            cartItems.map(item => (
              <div key={item.id} style={{
                display: 'flex', gap: '12px',
                padding: '14px 20px',
                borderBottom: '1px solid #f5f5f5',
                alignItems: 'flex-start',
              }}>
                {/* Product Image */}
                <div style={{
                  width: '72px', height: '72px', flexShrink: 0,
                  borderRadius: '4px', overflow: 'hidden',
                  border: '1px solid #eee', backgroundColor: '#f9f9f9',
                }}>
                  <img
                    src={item.image || 'https://via.placeholder.com/72x72/f5f5f5/ccc?text=?'}
                    alt={item.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={e => { e.target.src = 'https://via.placeholder.com/72x72/f5f5f5/ccc?text=?'; }}
                  />
                </div>

                {/* Product Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Name */}
                  <p style={{
                    fontSize: '13px', fontWeight: 600, color: '#282c3f',
                    marginBottom: '3px', lineHeight: '1.35',
                    overflow: 'hidden', textOverflow: 'ellipsis',
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                  }}>
                    {item.name}
                  </p>

                  {/* Price */}
                  <p style={{ fontSize: '14px', fontWeight: 700, color: '#282c3f', marginBottom: '8px' }}>
                    ৳{(item.price * item.quantity).toLocaleString()}
                    {item.quantity > 1 && (
                      <span style={{ fontSize: '11px', color: '#999', fontWeight: 400, marginLeft: '4px' }}>
                        ৳{Number(item.price).toLocaleString()} × {item.quantity}
                      </span>
                    )}
                  </p>

                  {/* Quantity + Delete */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {/* Quantity Controls */}
                    <div style={{
                      display: 'flex', alignItems: 'center',
                      border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden',
                    }}>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        style={{
                          width: '28px', height: '28px', border: 'none',
                          backgroundColor: '#f5f5f5', cursor: 'pointer',
                          fontSize: '18px', fontWeight: 700, color: '#282c3f',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          lineHeight: 1,
                        }}
                      >−</button>
                      <span style={{
                        width: '32px', textAlign: 'center',
                        fontSize: '13px', fontWeight: 600, color: '#282c3f',
                      }}>
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        style={{
                          width: '28px', height: '28px', border: 'none',
                          backgroundColor: '#f5f5f5', cursor: 'pointer',
                          fontSize: '18px', fontWeight: 700, color: '#282c3f',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          lineHeight: 1,
                        }}
                      >+</button>
                    </div>

                    {/* Delete Button */}
                    <button
                      onClick={() => removeFromCart(item.id)}
                      title="Remove item"
                      style={{
                        background: 'none', border: '1px solid #ffd0d8',
                        borderRadius: '4px', cursor: 'pointer',
                        width: '28px', height: '28px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#ff3f6c', flexShrink: 0,
                        transition: 'background-color 0.15s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = '#fff0f3'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div style={{
            borderTop: '2px solid #eee',
            padding: '16px 20px',
            backgroundColor: '#fff',
            flexShrink: 0,
          }}>
            {/* Total */}
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: '14px',
            }}>
              <span style={{ fontSize: '14px', color: '#666' }}>
                Total ({totalCount} items)
              </span>
              <span style={{ fontSize: '20px', fontWeight: 700, color: '#282c3f' }}>
                ৳{totalPrice.toLocaleString()}
              </span>
            </div>

            {/* Checkout */}
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/checkout', { state: { cartItems: cartItems } }); // ← cartItems পাঠান
              }}
              style={{
                width: '100%', padding: '14px',
                backgroundColor: '#ff3f6c', color: '#fff',
                border: 'none', borderRadius: '4px',
                fontSize: '14px', fontWeight: 700,
                letterSpacing: '0.08em', textTransform: 'uppercase',
                cursor: 'pointer', transition: 'background-color 0.2s',
                marginBottom: '8px',
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#e03560'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#ff3f6c'}
            >
              Proceed to Checkout →
            </button>

            {/* Continue Shopping */}
            <button
              onClick={() => setIsOpen(false)}
              style={{
                width: '100%', padding: '10px',
                backgroundColor: '#fff', color: '#282c3f',
                border: '1px solid #ddd', borderRadius: '4px',
                fontSize: '13px', fontWeight: 600,
                cursor: 'pointer', transition: 'background-color 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f5f5f5'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}
            >
              Continue Shopping
            </button>
          </div>
        )}
      </div>

      {/* ── Floating Cart Button ── */}
      <div style={{
        position: 'fixed',
        right: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 9997,
      }}>
        <button
          onClick={() => setIsOpen(true)}
          style={{
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            backgroundColor: '#282c3f', color: '#fff',
            width: '48px', minHeight: '68px',
            borderRadius: '8px 0 0 8px',
            boxShadow: '-2px 2px 12px rgba(0,0,0,0.2)',
            border: 'none', cursor: 'pointer',
            gap: '4px', padding: '10px 0',
            transition: 'background-color 0.2s',
            position: 'relative',
          }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#ff3f6c'}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = '#282c3f'}
        >
          {/* Count Badge */}
          {totalCount > 0 && (
            <span style={{
              position: 'absolute', top: '5px', right: '5px',
              backgroundColor: '#ff3f6c',
              color: '#fff', fontSize: '9px', fontWeight: 700,
              borderRadius: '50%', width: '16px', height: '16px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '1.5px solid #282c3f',
            }}>
              {totalCount > 99 ? '99+' : totalCount}
            </span>
          )}

          {/* Cart Icon */}
          <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>

          <span style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            Cart
          </span>
        </button>
      </div>
    </>
  );
}